/* =============================================================================
   ui.jsx  —  shared primitives used by both guest + admin.
============================================================================= */
import { useState, useEffect, useCallback } from 'react';
import { Sprig } from './decor';

/* ----------------------- async load with states -------------------------- */
// Wraps a DB read in loading / error / retry. Used everywhere data is fetched.
export function useAsyncData(loader, deps = []) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const run = useCallback(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    Promise.resolve()
      .then(loader)
      .then((data) => live && setState({ loading: false, error: null, data }))
      .catch((e) => {
        console.error(e);
        live && setState({ loading: false, error: e.message || 'Something went wrong.', data: null });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(run, [run]);
  return { ...state, reload: run };
}

/* small spinner / error block */
export function Loading({ label = 'Loading' }) {
  return (
    <div className="center muted" style={{ padding: '40px 0' }}>
      <div className="spinner" aria-hidden="true" />
      <div style={{ marginTop: 12, letterSpacing: '0.04em' }}>{label}...</div>
    </div>
  );
}
export function ErrorBlock({ error, onRetry }) {
  return (
    <div className="card card-soft center" style={{ borderColor: 'var(--ladybug)' }}>
      <p style={{ color: 'var(--ladybug)', fontWeight: 700, margin: '0 0 12px' }}>
        We could not load this just now.
      </p>
      <p className="muted" style={{ fontSize: '0.9rem' }}>{error}</p>
      {onRetry && (
        <button className="btn btn-secondary btn-sm" onClick={onRetry} style={{ marginTop: 8 }}>
          Try again
        </button>
      )}
    </div>
  );
}

/* ------------------------------ form field ------------------------------- */
export function Field({ label, required, optional, error, children, hint }) {
  return (
    <div className="field">
      {label && (
        <label className="label">
          {label}{' '}
          {required && <span className="req" aria-hidden="true">*</span>}
          {optional && <span className="opt">(optional)</span>}
        </label>
      )}
      {children}
      {hint && !error && <div className="faint" style={{ fontSize: '0.8rem', marginTop: 5 }}>{hint}</div>}
      {error && <div className="err-text">{error}</div>}
    </div>
  );
}

/* text input bound helper */
export function Input({ value, onChange, invalid, ...rest }) {
  return (
    <input
      className={invalid ? 'invalid' : ''}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}
export function TextArea({ value, onChange, invalid, ...rest }) {
  return (
    <textarea
      className={invalid ? 'invalid' : ''}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      {...rest}
    />
  );
}

/* ------------------------------- toggle ---------------------------------- */
export function Toggle({ on, onChange, label, sub }) {
  return (
    <label className="toggle-row">
      <div>
        <div className="toggle-label">{label}</div>
        {sub && <div className="faint" style={{ fontSize: '0.82rem' }}>{sub}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        className={`switch ${on ? 'on' : ''}`}
        onClick={() => onChange(!on)}
      >
        <span className="knob" />
      </button>
    </label>
  );
}

/* ------------------------- section heading block ------------------------- */
export function SectionHead({ eyebrow, title, lead, center = true }) {
  return (
    <div className={center ? 'center' : ''} style={{ marginBottom: 30 }}>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2 className="section-title">{title}</h2>
      <div className="divider"><Sprig /></div>
      {lead && <p className="section-lead" style={center ? { marginLeft: 'auto', marginRight: 'auto' } : {}}>{lead}</p>}
    </div>
  );
}

/* tiny confirmation/toast inline */
export function Note({ tone = 'sage', children, style }) {
  const colors = {
    sage: ['var(--sage-tint)', 'var(--botanical)'],
    warn: ['#fbeae0', 'var(--terra-deep)'],
    error: ['#f9e4e1', 'var(--ladybug)'],
  }[tone];
  return (
    <div style={{ background: colors[0], color: colors[1], borderRadius: 12, padding: '12px 16px', fontWeight: 600, fontSize: '0.92rem', ...style }}>
      {children}
    </div>
  );
}
